<script setup lang="ts">
const { getAllProjects, getFormations } = useProjects()

const projects = await getAllProjects()
const formations = computed(() => {
  const types = Array.from(new Set(projects.map(p => p.formation))).filter(Boolean)
  return ['Tous', ...types.sort()]
})
const activeFormation = ref('Tous')
const showOlderProjects = ref(false)

// Helper to get school year from date (Sept XXXX - Aug XXXX+1)
const getSchoolYear = (date: string | null | undefined): string => {
  if (!date) return 'Non daté'
  const d = new Date(date)
  const month = d.getMonth() // 0-11
  const year = d.getFullYear()
  // If month is Sept-Dec (8-11), it's year-year+1
  // If month is Jan-Aug (0-7), it's year-1-year
  if (month >= 8) {
    return `${year}-${year + 1}`
  } else {
    return `${year - 1}-${year}`
  }
}

// Get current school year
const getCurrentSchoolYear = (): string => {
  const now = new Date()
  return getSchoolYear(now.toISOString())
}

// Filter by formation first
const filteredByFormation = computed(() => {
  if (activeFormation.value === 'Tous') return projects
  return projects.filter(p => p.formation === activeFormation.value)
})

// Group projects by school year
const projectsByYear = computed(() => {
  const grouped: Record<string, typeof projects> = {}
  
  for (const project of filteredByFormation.value) {
    const year = getSchoolYear(project.realizationDate)
    if (!grouped[year]) grouped[year] = []
    grouped[year].push(project)
  }
  
  // Sort years descending
  const sortedYears = Object.keys(grouped).sort((a, b) => {
    if (a === 'Non daté') return 1
    if (b === 'Non daté') return -1
    return b.localeCompare(a)
  })
  
  return sortedYears.map(year => ({
    year,
    projects: grouped[year]
  }))
})

// Get the 3 most recent years
const recentYears = computed(() => projectsByYear.value.slice(0, 3))
const olderYears = computed(() => projectsByYear.value.slice(3))
const hasOlderProjects = computed(() => olderYears.value.length > 0)

const { setPageSeo } = useSeo()

await setPageSeo({
  title: 'Portfolio des projets étudiants',
  description: 'Explorez l\'univers des créations OXO : jeux vidéo, expériences interactives et innovations digitales.'
})
</script>

<template>
  <div class="bg-background text-foreground min-h-screen transition-colors duration-300">
    <UiPageHero 
      pageKey="projects"
      badge-text="Portfolio Étudiant"
      title-prefix="LEURS PREMIERS"
      title-highlight="SUCCÈS"
      description="Un aperçu concret des compétences acquises par nos élèves durant leurs deux cursus."
      :tags="['RÉALISATIONS', 'PROJETS', 'MÉTIER']"
      height="screen"
      align="left"
      :primary-button="{ text: 'INTÉGRER OXO', to: '/candidature', umamiEvent: 'Projets - Hero - Candidater' }"
      :secondary-button="{ text: 'RETOUR AUX CURSUS', to: '/formations', umamiEvent: 'Projets - Hero - Formations' }"
    />


    <section class="py-section-py relative" aria-label="Galerie des projets">
      <div class="container mx-auto px-container-px">
        
        <!-- Filter Tabs -->
        <div class="flex flex-col items-center gap-10 mb-section-header-mb animate-fade-in-up delay-300" role="search" aria-label="Filtres des projets">
          <!-- Mobile/Tablet Dropdown (< 1024px) -->
          <UiMobileSelect
            v-model="activeFormation"
            :options="formations"
            label="Filtrer par formation"
          />

          <!-- Desktop Buttons (>= 1024px) -->
          <div class="hidden lg:flex flex-wrap justify-center gap-3">
            <button
              v-for="formation in formations"
              :key="formation"
              @click="activeFormation = formation"
              class="relative px-8 py-3 rounded-xl font-black text-[10px] uppercase tracking-[0.2em] transition-all duration-500 overflow-hidden group"
              :class="activeFormation === formation 
                ? 'text-white' 
                : 'text-foreground-dim hover:text-foreground bg-oxo-card-bg/10'"
              :aria-pressed="activeFormation === formation"
            >
              <!-- Active/Hover Indicator -->
              <div 
                class="absolute inset-0 z-0 bg-oxo-red transition-transform duration-500"
                :class="activeFormation === formation ? 'scale-100' : 'scale-0 group-hover:scale-100 opacity-20 group-hover:opacity-10'"
              ></div>
              
              <span class="relative z-10">{{ formation }}</span>
            </button>
          </div>
          
          <div class="flex items-center gap-4 w-full max-w-lg">
             <div class="h-px flex-1 bg-oxo-border"></div>
             <span class="text-[10px] font-black uppercase tracking-widest text-foreground-dim/40">Explorer par année</span>
             <div class="h-px flex-1 bg-oxo-border"></div>
          </div>
        </div>

        <!-- Year Sections -->
        <div class="space-y-24">
          <template v-for="yearGroup in recentYears" :key="yearGroup.year">
            <section class="space-y-10" :aria-label="`Projets ${yearGroup.year}`">
              <!-- Year Header - Clean & Simple -->
              <header class="flex items-center gap-6 mb-2">
                <div class="flex items-center gap-3">
                  <div class="w-2 h-2 rounded-full bg-oxo-red"></div>
                  <h2 class="font-display font-black text-3xl md:text-4xl text-foreground">
                    {{ yearGroup.year }}
                  </h2>
                </div>
                <div class="h-px flex-1 bg-oxo-border"></div>
                <span class="text-sm text-foreground-dim">
                  {{ (yearGroup.projects ?? []).length }} projet{{ (yearGroup.projects ?? []).length > 1 ? 's' : '' }}
                </span>
              </header>
              
              <!-- Projects Grid -->
              <ul class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 list-none p-0">
                <li v-for="project in yearGroup.projects" :key="project.slug" class="w-full max-w-[240px] md:max-w-none mx-auto">
                  <ProjectCard :project="project" />
                </li>
              </ul>
            </section>
          </template>
          
          <!-- Older Projects Toggle -->
          <div v-if="hasOlderProjects && !showOlderProjects" class="flex justify-center">
            <button 
              @click="showOlderProjects = true"
              class="group flex items-center gap-4 px-8 py-4 clip-button bg-oxo-card-bg/30 border border-white/10 hover:border-oxo-red/50 transition-all duration-500 hover:shadow-[0_10px_40px_rgba(230,0,0,0.2)]"
            >
              <span class="text-sm font-black uppercase tracking-wider text-foreground-dim group-hover:text-white transition-colors">
                Voir les projets plus anciens
              </span>
              <div class="w-10 h-10 rounded-full bg-oxo-red/20 flex items-center justify-center group-hover:bg-oxo-red transition-colors duration-300">
                <i class="fas fa-chevron-down text-oxo-red group-hover:text-white transition-colors" aria-hidden="true"></i>
              </div>
            </button>
          </div>
          
          <!-- Older Years (Hidden by default) -->
          <template v-if="showOlderProjects">
            <template v-for="yearGroup in olderYears" :key="yearGroup.year">
              <section class="space-y-8" :aria-label="`Projets ${yearGroup.year}`">
                <!-- Year Header -->
                <header class="flex items-center gap-6">
                  <h2 class="font-display font-black text-4xl md:text-5xl text-white/60">
                    {{ yearGroup.year }}
                  </h2>
                  <div class="h-px flex-1 bg-linear-to-r from-white/20 via-oxo-border to-transparent"></div>
                  <span class="text-sm font-semibold text-foreground-dim/60">
                    {{ (yearGroup.projects ?? []).length }} projet{{ (yearGroup.projects ?? []).length > 1 ? 's' : '' }}
                  </span>
                </header>
                
                <!-- Projects Grid -->
                <ul class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 list-none p-0">
                  <li v-for="project in yearGroup.projects" :key="project.slug" class="w-full max-w-[240px] md:max-w-none mx-auto">
                    <ProjectCard :project="project" />
                  </li>
                </ul>
              </section>
            </template>
          </template>
        </div>

        <!-- Empty State -->
        <div v-if="projectsByYear.length === 0" class="flex flex-col items-center justify-center py-32 space-y-6 text-center">
            <div class="w-20 h-20 rounded-full bg-oxo-card-bg border border-oxo-border flex items-center justify-center text-foreground-dim/40">
                <i class="fas fa-search text-3xl" aria-hidden="true"></i>
            </div>
            <div class="space-y-1">
              <h3 class="text-xl font-bold text-foreground">Aucune archive trouvée</h3>
              <p class="text-foreground-dim font-light">Revenez bientôt pour découvrir de nouveaux projets.</p>
            </div>
        </div>

      </div>
    </section>

    <div class="h-px w-full bg-linear-to-r from-transparent via-oxo-border to-transparent"></div>

    <AppCTA
      title="Prêt à créer ?"
      titleHighlight="INSCRIS TOI !"
      subtitle="Transformez votre passion en expertise et devenez le prochain créateur d'univers."
      buttonText="Démarrer votre aventure"
    />
  </div>
</template>

<style scoped>

.delay-300 { animation-delay: 0.3s; }

</style>
